import * as React from "react";
import {
  Box, Typography, Button, useMediaQuery, Dialog, IconButton,
  AppBar, Toolbar, Container, Grid, Chip, Divider
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useTheme, alpha } from "@mui/material/styles";

import AudioCompare from "./AudioCompare";


/** ---------- Tube Amp Feature List ---------- */
function TubeAmpFeatureList({ title = "Amp features", features = [] }) {
  const theme = useTheme();

  const items =
    features.length > 0
      ? features
      : [
        { label: "All-tube preamp & power section", caption: "Authentic harmonic content & touch response" },
        { label: "Class A / AB topologies", caption: "Choose chimey single-ended or punchy push-pull" },
        { label: "Real power-supply sag", caption: "Natural compression when you dig in" },
        { label: "Selectable negative feedback", caption: "Tight & modern ↔ open & vintage" },
        { label: "Tube rectifier or solid-state", caption: "Feel options for attack & bloom" },
        { label: "Dedicated FX loop", caption: "Time-based effects stay clear at any gain" },
        { label: "Master volume / PPIMV", caption: "Cranked tones at manageable levels" },
        { label: "External bias test points", caption: "Easy tube swaps & maintenance" },
        { label: "Multiple speaker outs", caption: "4/8/16Ω taps for cab flexibility" },
        { label: "Hi/Lo inputs", caption: "Gain staging for single-coils or humbuckers" },
        { label: "Standby & mute", caption: "Warm-up protection and silent patching" },
        { label: "Hand-wired construction", caption: "Serviceable layout with premium components" },
      ];

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5 }}>
        {title}
      </Typography>

      <Grid container spacing={1.5} sx={{ mt: 1 }}>
        {items.map((it, i) => (
          <Grid key={i} item xs={12} sm={6}>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "flex-start",
                p: 1,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: alpha(theme.palette.background.paper, 0.6),
                backdropFilter: "saturate(140%) blur(4px)",
                // transition: "border-color 120ms ease, background-color 120ms ease",
                // "&:hover": {
                //   borderColor: theme.palette.text.secondary,
                //   backgroundColor: alpha(theme.palette.background.paper, 0.75),
                // },
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: 1.4 }}>
                  {it.label}
                </Typography>
                {it.caption && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25, lineHeight: 1.4 }}>
                    {it.caption}
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

/** ---------- Design Story Content (inherits page background) ---------- */
function DesignStoryContent({
  heroKicker = "Design Story",
  heroTitle = "Designed to feel analog. Built for the modern player.",
  heroBody = "We started with what guitar players love about classic amps—the touch, the dynamics, the way notes bloom—and rebuilt it with precision DSP so you can have it anywhere, anytime.",
  sections = [],
  principles = ["Clarity over clutter", "Feel before features", "Sound you can trust"],
  features = [],
  rootSx, // optional: override modal content background/styles
}) {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  return (
    <Box component="section" sx={{ minHeight: "100vh", color: theme.palette.text.primary, ...rootSx }}>
      <Container sx={{ py: { xs: 8, md: 12 } }}>
        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 2 }}>
          {heroKicker}
        </Typography>
        <Typography variant={isMdUp ? "h3" : "h4"} sx={{ letterSpacing: -0.5, mt: 1, fontWeight: 700, maxWidth: 900 }}>
          {heroTitle}
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mt: 2, maxWidth: 900, lineHeight: 1.6 }}>
          {heroBody}
        </Typography>

        <Box sx={{ mt: 3, display: "flex", gap: 1, flexWrap: "wrap" }}>
          {principles.map((p) => (
            <Chip key={p} label={p} variant="outlined" />
          ))}
        </Box>


      </Container>

      <Divider />

      <Container sx={{ py: { xs: 6, md: 10 } }}>
        {sections.map((s, i) => {
          const alignRight = s.align === "right";
          return (
            <Grid
              container
              key={i}
              spacing={4}
              alignItems="center"
              direction={alignRight ? "row-reverse" : "row"}
              sx={{ mb: { xs: 6, md: 10 } }}
            >
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    borderRadius: 3,
                    overflow: "hidden",
                    border: `1px solid ${theme.palette.divider}`, // hairline frame
                  }}
                >
                  {s.image ? (
                    <Box
                      component="img"
                      src={s.image}
                      alt={s.alt || s.title}
                      sx={{ display: "block", width: "100%", height: { xs: 260, md: 420 }, objectFit: "cover" }}
                    />
                  ) : (
                    <Box sx={{ width: "100%", height: { xs: 260, md: 420 }, bgcolor: alpha(theme.palette.text.primary, 0.06) }} />
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="h4" sx={{ letterSpacing: -0.3, fontWeight: 700 }}>
                  {s.title}
                </Typography>

                <Typography variant="body1" color="text.secondary" sx={{ mt: 1.5, lineHeight: 1.75 }}>
                  {s.body}
                </Typography>

                {/* ▶️ Audio: quick-compare — plays all in sync, only one audible */}
                <AudioCompare section={s} />
              </Grid>
            </Grid>
          );
        })}
        <TubeAmpFeatureList features={features} />
      </Container>
    </Box>
  );
}

/** ---------- Hero with CTA → opens Design Story modal ---------- */
export default function ImageBackgroundSection(props) {
  const {
    image,
    mobileImage,
    headerImage,
    headerImageAlt = "",
    title,
    description,
    ctaLabel = "Explore this amp",
    align = "right",
    imageEnterFrom = "left",
    imageEnterFromMobile,
    heightDesktop = "100vh",
    heightMobile = "100vh",
    overlayColor = "rgba(0,0,0,0.25)",
    overlayMobileColor = "rgba(0,0,0,0.55)", // static overlay for mobile
    once = true,

    // design story data + optional styling overrides
    designStory = {
      heroKicker: "Design Story",
      heroTitle: "Designed to feel analog. Built for the modern player.",
      heroBody:
        "We started with what guitar players love about classic amps—the touch, the dynamics, the way notes bloom—and rebuilt it with precision DSP so you can have it anywhere, anytime.",
      sections: [],
      principles: ["Clarity over clutter", "Feel before features", "Sound you can trust"],
      features: [
        { label: "All-tube preamp & power section", caption: "Authentic harmonic content & touch response" },
        { label: "Class A / AB topologies", caption: "Chime or punch—pick your feel" },
        { label: "Real power-supply sag", caption: "Natural compression when you dig in" },
        { label: "Selectable negative feedback", caption: "Tight ↔ open voicings" },
        { label: "Tube/SS rectifier options", caption: "Attack & bloom control" },
        { label: "Series FX loop", caption: "Delays & reverbs stay clear" },
        { label: "Master volume / PPIMV", caption: "Stage or studio levels" },
        { label: "Bias test points", caption: "Easy maintenance" },
        { label: "4/8/16Ω speaker taps", caption: "Cab flexibility" },
        { label: "Hi/Lo inputs", caption: "Pickup-friendly gain staging" },
        { label: "Standby & mute", caption: "Warm-up & silent patching" },
        { label: "Hand-wired build", caption: "Serviceable, road-ready" },
      ],
    },
    storyRootSx,     // control modal content bg (e.g., { bgcolor: "transparent" })
    dialogPaperSx,   // control dialog paper bg
    appBarSx,        // control top bar look
  } = props;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const imgSrc = isMobile && mobileImage ? mobileImage : image;

  const ref = React.useRef(null);
  const [inView, setInView] = React.useState(false);
  const [openStory, setOpenStory] = React.useState(false);

  const prefersReduced =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  React.useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const hit = entries[0]?.isIntersecting;
        if (hit) setInView(true);
        else if (!once) setInView(false);
      },
      { threshold: 0.25 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [once]);

  const isRight = align === "right";

  // Desktop gradient from text side (your original logic)
  const overlayGradient = !isMobile
    ? (isRight
      ? `linear-gradient(to left, ${overlayColor} 0%, ${overlayColor} 12%, rgba(0,0,0,0) 100%)`
      : `linear-gradient(to right, ${overlayColor} 0%, ${overlayColor} 12%, rgba(0,0,0,0) 100%)`)
    : undefined;

  const enterDir = (isMobile ? imageEnterFromMobile || imageEnterFrom : imageEnterFrom).toLowerCase();
  const initialTransform =
    enterDir === "right" ? "translateX(8%)"
      : enterDir === "top" ? "translateY(-8%)"
        : enterDir === "bottom" ? "translateY(8%)"
          : "translateX(-8%)";
  const finalTransform = "translateX(0) translateY(0)";

  // Desktop overlay animates from text side; mobile static
  const desktopOverlayStyle = {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    background: overlayGradient,
    transformOrigin: isRight ? "right center" : "left center",
    transform: prefersReduced ? "none" : `scaleX(${inView ? 1 : 0})`,
    transition: prefersReduced ? "none" : "transform 900ms ease",
  };
  const mobileOverlayStyle = {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    backgroundColor: overlayMobileColor,
  };

  return (
    <>
      {/* HERO */}
      <Box
        ref={ref}
        sx={{
          position: "relative",
          height: { xs: heightMobile, md: heightDesktop },
          width: "100%",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: isMobile ? "center" : (isRight ? "flex-end" : "flex-start"),
          color: "white",
        }}
      >
        {/* Background image */}
        <Box
          component="img"
          src={imgSrc}
          alt={typeof title === "string" ? title : "Background"}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center",
            filter: prefersReduced ? "none" : "saturate(105%)",
            transform: prefersReduced ? finalTransform : (inView ? finalTransform : initialTransform),
            opacity: prefersReduced ? 1 : (inView ? 1 : 0.85),
            transition: prefersReduced ? "none" : "transform 900ms ease, opacity 900ms ease",
            willChange: "transform, opacity",
          }}
        />

        {/* Overlay */}
        <Box aria-hidden sx={isMobile ? mobileOverlayStyle : desktopOverlayStyle} />

        {/* Content */}
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            width: { xs: "90%", sm: "80%", md: "50%" },
            textAlign: isMobile ? "center" : (isRight ? "right" : "left"),
            px: { xs: 3, sm: 5, md: 10 },
            opacity: prefersReduced ? 1 : inView ? 1 : 0,
            transform: prefersReduced
              ? "none"
              : inView
                ? "translateX(0)"
                : isMobile
                  ? "translateY(12px)"
                  : isRight
                    ? "translateX(24px)"
                    : "translateX(-24px)",
            transition: prefersReduced ? "none" : "opacity 600ms ease 200ms, transform 600ms ease 200ms",
            display: "flex",
            flexDirection: "column",
            alignItems: isMobile ? "center" : isRight ? "flex-end" : "flex-start",
          }}
        >
          {headerImage && (
            <Box
              component="img"
              src={headerImage}
              alt={headerImageAlt || (typeof title === "string" ? `${title} logo` : "Header image")}
              sx={{ width: { xs: "80%", sm: "70%", md: "60%" }, maxWidth: 600, height: "auto", mb: 3, objectFit: "contain" }}
            />
          )}

          <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, lineHeight: 1.2 }}>
            {title}
          </Typography>

          <Typography variant="h6" sx={{ mb: 3, color: "rgba(255,255,255,0.92)", lineHeight: 1.6 }}>
            {description}
          </Typography>

          {ctaLabel && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpenStory(true)}
              sx={{
                px: 4,
                py: 1.25,
                borderRadius: 3,
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 600,
                boxShadow: "none",
                backgroundColor: "rgba(255,255,255,0.75)",
                backdropFilter: "blur(6px)",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.9)" },
                mx: isMobile ? "auto" : 0,
                display: isMobile ? "block" : "inline-flex",
              }}
            >
              {ctaLabel}
            </Button>
          )}
        </Box>
      </Box>

      {/* FULL-SCREEN DIALOG */}
      <Dialog
        fullScreen
        open={openStory}
        onClose={() => setOpenStory(false)}  // Esc key will also close
        keepMounted
        BackdropProps={{
          onClick: () => setOpenStory(false), // tap outside to close
          sx: {
            backdropFilter: "saturate(160%) blur(8px)",
            backgroundColor: "rgba(0,0,0,0.25)",
          },
        }}
        PaperProps={{
          sx: {
            bgcolor: "background.default",
            ...dialogPaperSx,
          },
        }}
      >
        {/* Top bar (empty visual header, 80px tall) */}
        <AppBar
          elevation={0}
          color="transparent"
          sx={{
            position: "sticky",
            top: 0,
            zIndex: (t) => t.zIndex.modal + 1,
            background: alpha((dialogPaperSx && dialogPaperSx.bgcolor) || theme.palette.background.paper, 0.7),
            backdropFilter: "saturate(160%) blur(10px)",
            borderBottom: `1px solid ${theme.palette.divider}`,
            ...appBarSx,
          }}
        >
          <Toolbar
            disableGutters
            sx={{
              height: 80,
              minHeight: 80,
              px: 2,
              paddingTop: "max(env(safe-area-inset-top), 0px)",
            }}
          />
        </AppBar>

        {/* Floating Close below the top bar */}
        <IconButton
          edge="end"
          onClick={() => setOpenStory(false)}
          aria-label="Close"
          sx={{
            position: "fixed",
            top: 80, // 80px below top
            right: 16,
            zIndex: (t) => t.zIndex.modal + 2,
            mr: "env(safe-area-inset-right)",
            backgroundColor: "rgba(0,0,0,0.4)",
            color: "white",
            "&:hover": { backgroundColor: "rgba(0,0,0,0.6)" },
          }}
        >
          <CloseRoundedIcon />
        </IconButton>

        <DesignStoryContent
          heroKicker={designStory.heroKicker}
          heroTitle={designStory.heroTitle}
          heroBody={designStory.heroBody}
          sections={designStory.sections}
          principles={designStory.principles}
          features={designStory.features}
          rootSx={storyRootSx}
        />
      </Dialog>
    </>
  );
}

/**
 * —— Usage (example) ——
 * Pass each section with an `audios` array. Backward compatible with `audioSrc/audioLabel`.
 *
 * <ImageBackgroundSection
 *   image="/hero.jpg"
 *   title="Nebula 30"
 *   description="Vintage sparkle. Modern control."
 *   designStory={{
 *     sections: [
 *       {
 *         title: "American Clean",
 *         body: "Bright, glassy...",
 *         image: "/clean.jpg",
 *         audios: [
 *           { src: "/audio/clean_strum.mp3", label: "Strum clean" },
 *           { src: "/audio/clean_arpeggio.mp3", label: "Arpeggio clean", caption: "Neck pickup" },
 *         ],
 *       },
 *     ],
 *     features: [
 *       { label: "KT66/6L6/EL34-ready power section", caption: "Voice the amp for the gig", badge: "Swap-friendly" },
 *       { label: "Half-power switch", caption: "Tames volume without killing feel" },
 *       { label: "Presence & resonance", caption: "Fine-tune top & low-end punch" },
 *     ],
 *   }}
 * />
 */
